/**
 * PersonaSwitcher — header dropdown that lets the Master jump between:
 *   • the Master view itself
 *   • any of the 5 Demo Profiles (Trial-locked + 4 Growth)
 *
 * Each demo entry has TWO actions:
 *   1. "Open" — issues an impersonation JWT (super_admin only) and opens
 *      the persona's session in a NEW tab. Master keeps their own tab.
 *   2. "Magic link" — generates a 14-day magic URL and copies it to clipboard.
 *
 * Mounted in MasterLayout's header so it's reachable from every Master page.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronDown, Crown, Users, ExternalLink, Wand2, Loader2, Lock, ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PersonaSwitcher() {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || accounts.length) return;
    setLoading(true);
    axios.get(`${API}/master/demo-accounts`)
      .then((r) => setAccounts(r.data.accounts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, accounts.length]);

  const impersonate = async (tenantId, label, sameTab = false) => {
    try {
      const { data } = await axios.post(`${API}/master/tenants/${tenantId}/impersonate`);
      if (!data?.access_token) { toast.error("No token returned"); return; }
      const url = `/impersonate?token=${encodeURIComponent(data.access_token)}&dest=/dashboard${sameTab ? "&backup=1" : ""}`;
      if (sameTab) {
        window.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
        toast.success(`Opened as ${label}`);
        setOpen(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const copyMagicLink = async (slug, label) => {
    try {
      const { data } = await axios.post(`${API}/auth/magic-link?slug=${slug}&ttl_days=14`);
      await navigator.clipboard.writeText(data.url);
      toast.success(`Magic link for ${label} copied · 14d valid`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="relative" data-testid="persona-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="persona-switcher-trigger"
        className="flex items-center gap-2 h-9 px-3 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 hover:border-[#C9A24E]/40 transition-colors text-[12.5px] text-white"
      >
        <Crown className="w-3.5 h-3.5 text-[#C9A24E]" />
        <span className="font-bold">Master</span>
        <span className="text-white/45">·</span>
        <span className="text-white/65">Switch persona</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[380px] rounded-xl border border-white/10 shadow-2xl overflow-hidden z-40"
               style={{ background: "rgba(15,8,32,0.97)", backdropFilter: "blur(12px)" }}
               data-testid="persona-switcher-menu">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-[#C9A24E]" />
              <h3 className="text-[12px] font-bold text-white uppercase tracking-wider">Demo Profiles</h3>
              <span className="text-[10px] text-white/45 ml-auto">5 personas</span>
            </div>

            {loading ? (
              <div className="py-8 text-center text-white/55 inline-flex items-center justify-center gap-2 w-full">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : accounts.length === 0 ? (
              <div className="px-4 py-6 text-[12px] text-white/55 text-center">
                No demo accounts yet — go to <b className="text-[#C9A24E]">Demo Profiles</b> and click "Seed".
              </div>
            ) : (
              <ul className="py-1 max-h-[440px] overflow-y-auto">
                {accounts.map((a) => {
                  const isTrial = a.package === "personalizado";
                  return (
                    <li key={a.tenant_id}
                        data-testid={`persona-row-${a.slug}`}
                        className="px-4 py-2.5 hover:bg-white/[0.04] transition-colors">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${isTrial ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                          {isTrial ? <Lock className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-bold text-white truncate">{a.label}</div>
                          <div className="text-[10.5px] text-white/55 truncate">{a.email}</div>
                        </div>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${isTrial ? "bg-rose-500/20 text-rose-200" : "bg-emerald-500/20 text-emerald-200"}`}>
                          {a.package}
                        </span>
                      </div>
                      <div className="flex gap-1.5 ml-9">
                        <button onClick={() => impersonate(a.tenant_id, a.label, true)}
                                data-testid={`persona-here-${a.slug}`}
                                title="Abrir nesta aba (com banner amarelo de retorno)"
                                className="h-7 px-2 rounded bg-amber-400 hover:bg-amber-500 text-[#1A0E36] text-[11px] font-bold inline-flex items-center justify-center gap-1">
                          Here
                        </button>
                        <button onClick={() => impersonate(a.tenant_id, a.label, false)}
                                data-testid={`persona-open-${a.slug}`}
                                title="Abrir em nova aba"
                                className="flex-1 h-7 px-2 rounded bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[11px] font-bold inline-flex items-center justify-center gap-1">
                          <ExternalLink className="w-3 h-3" /> New tab
                        </button>
                        <button onClick={() => copyMagicLink(a.slug, a.label)}
                                data-testid={`persona-magic-${a.slug}`}
                                className="flex-1 h-7 px-2 rounded bg-[#F59E0B] hover:bg-[#D97706] text-[#1A0E36] text-[11px] font-bold inline-flex items-center justify-center gap-1">
                          <Wand2 className="w-3 h-3" /> Magic link
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="px-4 py-2.5 border-t border-white/10 text-[11px] text-white/55 text-center">
              <a href="/master/demo-accounts" className="hover:text-[#C9A24E] font-bold">
                Manage demo profiles →
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
